export function buildShareEarningsText(formattedEarnings = '') {
    return formattedEarnings ? `🌳 ${formattedEarnings} earned on Treechat` : ''
}

// Caption for the daily-earnings share: the aggregate day, not one post.
export function buildDailyEarningsShareText(formattedEarnings = '') {
    return formattedEarnings ? `🌳 ${formattedEarnings} earned today on Treechat` : ''
}

export function selectShareEarningsLabel({
    formattedSatsEarned = '',
    usdEarned = null,
    formattedUsdEarned = '',
}) {
    if (Number.isFinite(usdEarned) && usdEarned > 0.1 && formattedUsdEarned) {
        return formattedUsdEarned
    }

    return formattedSatsEarned
}

export function buildSharePayload({ shareUrl, shareText }) {
    return {
        ...(shareText ? { text: shareText } : {}),
        url: shareUrl,
    }
}

export function buildShareDestination({ destination, shareUrl, shareText, shareTitle = '' }) {
    if (destination === 'native') {
        return {
            kind: 'native',
            payload: buildSharePayload({ shareUrl, shareText }),
        }
    }

    let baseUrl
    let params

    switch (destination) {
        case 'x':
            baseUrl = 'https://twitter.com/intent/tweet'
            params = new URLSearchParams({ url: shareUrl })
            if (shareText) params.set('text', shareText)
            break
        case 'facebook':
            baseUrl = 'https://www.facebook.com/sharer/sharer.php'
            params = new URLSearchParams({ u: shareUrl, quote: shareText || '' })
            break
        case 'reddit':
            baseUrl = 'https://www.reddit.com/submit'
            params = new URLSearchParams({
                url: shareUrl,
                title: shareText || shareTitle || '',
            })
            break
        case 'telegram':
            baseUrl = 'https://t.me/share/url'
            params = new URLSearchParams({ url: shareUrl })
            if (shareText) params.set('text', shareText)
            break
        case 'email':
            baseUrl = 'mailto:'
            params = new URLSearchParams({
                subject: shareTitle || '',
                body: shareText ? `${shareText}\n\n${shareUrl}` : shareUrl,
            })
            break
        default:
            throw new RangeError(`Unknown share destination: ${destination}`)
    }

    return { kind: 'url', url: `${baseUrl}?${params.toString()}` }
}

export function resolveAnswerShareLaunchSurface({
    isMobile = false,
    hasShareTray = false,
    hasNativeShare = false,
    hasBrowserShare = false,
    hasDirectShareUrl = false,
}) {
    if (!hasDirectShareUrl) return 'public-link'
    if (!isMobile && hasShareTray) return 'tray'
    if (hasNativeShare) return 'native'
    if (hasBrowserShare) return 'browser'
    if (hasShareTray) return 'tray'

    return 'public-link'
}

export function resolveAnswerShareSurface({
    isMobileNativeFlow = false,
    canUseNativeShare = false,
    canShareImageCard = false,
    shareCardPreviewLoading = false,
    shareCardReady = false,
    shareCardFailed = false,
    shareCardSharing = false,
}) {
    const shareCardHeroVisible = canShareImageCard && !shareCardFailed
    const shareCardHeroReady = isMobileNativeFlow
        ? canUseNativeShare
        : shareCardReady && !shareCardPreviewLoading
    const shareCardHeroActionable = shareCardHeroVisible && shareCardHeroReady && !shareCardSharing

    return {
        shareCardHeroVisible,
        shareCardHeroActionable,
        shareCardHeroDisabled: !shareCardHeroActionable,
        showNativeFallback: isMobileNativeFlow && canUseNativeShare && !shareCardHeroVisible,
        showDesktopDestinations: !isMobileNativeFlow,
    }
}

export function resolveShareOrigin({
    locationOrigin = window?.location?.origin,
    host = gon?.host,
    protocol = document?.location?.protocol,
} = {}) {
    if (locationOrigin) {
        return locationOrigin.replace(/\/$/, '')
    }

    if (host && protocol) {
        return `${protocol}//${host}`
    }

    return null
}

// Utility function to generate share URLs for quests
export function getShareUrl(questId) {
    if (!questId) {
        console.error('getShareUrl: No quest ID provided')
        return null
    }

    try {
        const origin = resolveShareOrigin()
        if (!origin) return null
        return `${origin}/p/${questId}`
    } catch (error) {
        console.error('getShareUrl: Error generating share URL', error)
        return null
    }
}

function getExplicitAnswerHomeQuestKey(answer) {
    if (!answer) return null

    return (
        answer.home_quest?.key ||
        answer.home_quest?.id ||
        answer.home_quest_key ||
        answer.home_quest_id ||
        null
    )
}

function getAnswerUptreeQuestKey(answer) {
    return answer?.uptree_quest_id || null
}

function getAnswerDirectQuestKey(answer) {
    return answer?.quest_id || answer?.quest?.key || answer?.quest?.id || null
}

function getAnswerCanonicalQuestKey(answer) {
    return (
        getAnswerDirectQuestKey(answer) ||
        getExplicitAnswerHomeQuestKey(answer) ||
        getAnswerUptreeQuestKey(answer) ||
        (answer?.message_type === 'ordinal_wrapper' ? getAnswerDowntreeQuestKey(answer) : null)
    )
}

function getAnswerDowntreeQuestKey(answer) {
    return answer?.downtree_quest_id || answer?.child_quests?.[0]?.id || null
}

function getAnswerPathQuestKey(answer) {
    const answerPath = typeof answer?.path === 'string' ? answer.path : null
    const questAnswerPathMatch = answerPath?.match(/^\/quest\/([^/]+)\/answer\/[^/]+\/?$/)

    return questAnswerPathMatch?.[1] || null
}

function shouldUseExplicitQuestKeyOverPath(answer, pathQuestKey, explicitQuestKey) {
    if (!pathQuestKey || !explicitQuestKey) return false
    if (String(pathQuestKey) === String(explicitQuestKey)) return false

    const downtreeQuestKey = getAnswerDowntreeQuestKey(answer)

    return !!downtreeQuestKey && String(pathQuestKey) === String(downtreeQuestKey)
}

export function answerEmbedsHavePermalinkTargets(answer) {
    const embeds = Array.isArray(answer?.embeds) ? answer.embeds : []

    return embeds.every(embed => {
        if (!embed || typeof embed !== 'object') return false
        if (embed.id === '__PRIVATE__') return true

        return !!getAnswerPermalinkQuestKey(embed)
    })
}

export function getAnswerPermalinkQuestKey(answer) {
    if (!answer) return null

    const pathQuestKey = getAnswerPathQuestKey(answer)
    const explicitQuestKey = getAnswerCanonicalQuestKey(answer)
    if (pathQuestKey) {
        return shouldUseExplicitQuestKeyOverPath(answer, pathQuestKey, explicitQuestKey)
            ? explicitQuestKey
            : pathQuestKey
    }

    if (explicitQuestKey) return explicitQuestKey

    return null
}

export function getAnswerPermalinkPath(answer) {
    if (!answer) return null

    const answerPath = typeof answer.path === 'string' ? answer.path : null
    const pathQuestKey = getAnswerPathQuestKey(answer)
    const explicitQuestKey = getAnswerCanonicalQuestKey(answer)

    if (answer.id && shouldUseExplicitQuestKeyOverPath(answer, pathQuestKey, explicitQuestKey)) {
        return `/quest/${explicitQuestKey}/answer/${answer.id}`
    }

    if (pathQuestKey) {
        return answerPath.replace(/\/$/, '')
    }

    if (explicitQuestKey && answer.id) {
        return `/quest/${explicitQuestKey}/answer/${answer.id}`
    }

    if (answerPath?.startsWith('/answer/')) return answerPath

    const homeQuestKey = getAnswerPermalinkQuestKey(answer)

    if (homeQuestKey && answer.id) {
        return `/quest/${homeQuestKey}/answer/${answer.id}`
    }

    const answerKey = answer.key || answer.id
    if (!answerKey) return null

    return `/answer/${answerKey}`
}

export function getAnswerShareUrl(answer) {
    const path = getAnswerPermalinkPath(answer)
    if (!path) {
        console.error('getAnswerShareUrl: Answer has no permalink target')
        return null
    }

    try {
        const origin = resolveShareOrigin()
        if (!origin) return null
        return `${origin}${path}`
    } catch (error) {
        console.error('getAnswerShareUrl: Error generating answer permalink URL', error)
        return null
    }
}

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
const EXACT_UUID_PATTERN = new RegExp(`^${UUID_PATTERN.source}$`, 'i')
const SHARE_CARD_PRESETS = new Set(['landscape', 'square', 'story'])
const SHOWCASE_SHARE_KINDS = new Set(['profile', 'stream', 'meme'])

// Stand-alone external share/copy URL: /p/<answerId>, one UUID, no context.
// The server resolves the answer to its canonical thread context (its child
// quest when publicly previewable, else its containing thread with the answer
// anchored) and redirects before the SPA boots.
// Falls back to the long context-explicit permalink for non-UUID ids.
export function getAnswerExternalShareUrl(answer) {
    const answerId = answer?.id

    if (answerId && EXACT_UUID_PATTERN.test(String(answerId))) {
        const origin = resolveShareOrigin()
        if (origin) return `${origin}/p/${answerId}`
    }

    return getAnswerShareUrl(answer)
}

// Stand-alone daily-earnings share link: /p/<shareId>, same namespace as post
// short links. The server renders the earnings og tags for that id and the
// SPA resolves it to the sharer's profile.
export function getEarningsShareUrl(shareId) {
    if (!shareId || !EXACT_UUID_PATTERN.test(String(shareId))) return null

    const origin = resolveShareOrigin()
    if (!origin) return null
    return `${origin}/p/${shareId}`
}

// Public on-demand daily-earnings card for the share tray, with the same
// non-redirecting error contract as getAnswerShareCardUrl.
export function getEarningsShareCardUrl(shareId) {
    if (!shareId || !EXACT_UUID_PATTERN.test(String(shareId))) return null

    const origin = resolveShareOrigin()
    if (!origin) return null

    const params = new URLSearchParams({ fallback: 'error' })
    return `${origin}/og/earnings/${shareId}?${params.toString()}`
}

// Public on-demand image card used by the share tray. Answer ids deliberately
// use the same stand-alone context resolution as /p/<answerId>, so the server
// remains authoritative about the public thread/reply context.
export function getAnswerShareCardUrl(answer, preset = 'landscape') {
    const answerId = answer?.id
    if (!answerId || !EXACT_UUID_PATTERN.test(String(answerId))) return null
    if (!SHARE_CARD_PRESETS.has(preset)) return null

    const origin = resolveShareOrigin()
    if (!origin) return null

    // The share tray needs an actual post card. A non-redirecting error keeps
    // native downloaders from following the crawler fallback and sharing the
    // static Treechat logo instead.
    const params = new URLSearchParams({ preset, fallback: 'error' })
    const sourceVersion = [answer.updated_at, answer.bsv_upvalue_amt]
        .filter(value => value !== null && value !== undefined && value !== '')
        .join('-')
    if (sourceVersion) params.set('v', sourceVersion)
    return `${origin}/og/quest/${answerId}?${params.toString()}`
}

function getShowcaseShareSegment(kind, key) {
    if (!SHOWCASE_SHARE_KINDS.has(kind)) return null

    const normalizedKey = String(key || '').trim()
    if (!normalizedKey) return null

    const pathKey = kind === 'meme' ? normalizedKey.replace(/\s+/g, '-') : normalizedKey
    return encodeURIComponent(pathKey)
}

export function getShowcaseShareUrl(kind, key) {
    const segment = getShowcaseShareSegment(kind, key)
    if (!segment) return null

    const origin = resolveShareOrigin()
    if (!origin) return null

    const route = kind === 'profile' ? 'user' : kind
    return `${origin}/${route}/${segment}`
}

export function getShowcaseShareCardUrl(kind, key, preset = 'landscape') {
    const segment = getShowcaseShareSegment(kind, key)
    if (!segment || !SHARE_CARD_PRESETS.has(preset)) return null

    const origin = resolveShareOrigin()
    if (!origin) return null

    const params = new URLSearchParams({ preset, fallback: 'error' })
    return `${origin}/og/${kind}/${segment}?${params.toString()}`
}
